import { Box } from '@mui/material'
import { ColDef, ICellRendererParams } from 'ag-grid-community'
import { getCertificateHistory } from 'api/certificates'
import columnRenderers from 'components/column-renderers'
import InfinityAgGrid from 'components/table/InfinityAgGrid'
import TableToolbar from 'components/table/TableToolbar'
import { useGridControl } from 'hooks/useGridControl'
import { useCallback, useMemo } from 'react'
import { BaseTableRequest, CertificateHistoryRow, Option, UserTypeLabels } from 'types'
import { useGetDataSource } from 'utils'
import {
  certificateHistoryCellValueRenderer,
  certificateHistoryFieldNameRenderer,
} from '../../models/Certificates.model'

export function CertificatesHistory({
  id,
  coursesOptions,
  lessonsOptions,
}: {
  id: number
  coursesOptions: Option[]
  lessonsOptions: Option[]
}) {
  const getUserHistoryCallback = useCallback(
    (params: BaseTableRequest) => {
      const sort = params.sort || { field: 'updatedAt', sort: 'desc' }
      return getCertificateHistory({
        ...params,
        params: {
          id,
        },
        filter: {
          ...(params.filter || {}),
        },
        sort,
      })
    },
    [id],
  )

  const getDataSource = useGetDataSource(getUserHistoryCallback)

  const { gridRef, onGridReady } = useGridControl(getDataSource)

  const columnDefs: ColDef[] = useMemo(() => {
    const columns: ColDef[] = [
      {
        headerName: 'Action',
        field: 'action',
        maxWidth: 120,
        cellRenderer: columnRenderers.capitalizeRenderer('action'),
      },
      {
        headerName: 'Field',
        field: 'field',
        cellRenderer: certificateHistoryFieldNameRenderer('field'),
        minWidth: 140,
      },
      {
        headerName: 'Old Value',
        field: 'oldValue',
        cellRenderer: certificateHistoryCellValueRenderer(
          'oldValue',
          coursesOptions,
          lessonsOptions,
        ),
        minWidth: 300,
      },
      {
        headerName: 'New Value',
        field: 'newValue',
        cellRenderer: certificateHistoryCellValueRenderer(
          'newValue',
          coursesOptions,
          lessonsOptions,
        ),
        minWidth: 300,
      },
      { headerName: 'Date', field: 'updatedAt', cellRenderer: columnRenderers.date('updatedAt') },
      {
        headerName: 'Updated by',
        field: 'updater',
        cellRenderer: (props: ICellRendererParams<CertificateHistoryRow>) => {
          const { firstname, lastname, email, type } = props?.data?.updater || {}

          let value = `${firstname} ${lastname}`
          if (type?.length) {
            value += ' / ' + type.map((t) => UserTypeLabels[t]).join(', ')
          }
          value = email ? value + ' / ' + email : value
          return value
        },
        minWidth: 450,
      },
    ]
    return columns
  }, [coursesOptions, lessonsOptions])

  return (
    <div>
      <TableToolbar title="History" />
      <Box height="800px">
        <InfinityAgGrid
          pagination
          innerRef={gridRef}
          columnDefs={columnDefs}
          onGridReady={onGridReady}
        />
      </Box>
    </div>
  )
}
